import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { TemplateApiService } from '../services/template-api.service';
import { TemplateCategoryApiService } from '../services/template-category-api.service';
import { TemplateCategoryModalComponent } from '../template-category-modal/template-category-modal.component';
import {
  Template,
  TemplateCategory,
  TemplateStatus,
} from '../models/template.model';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { DisableWithoutPermissionDirective } from '../../../shared/directives/disable-without-permission.directive';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

/** Iconos de visibilidad del badge (candado = personal, personas = pública). */
const VIS_ICON_PERSONAL =
  '<svg viewBox="0 0 20 20" fill="currentColor" class="h-3 w-3"><path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd"/></svg>';
const VIS_ICON_PUBLIC =
  '<svg viewBox="0 0 20 20" fill="currentColor" class="h-3 w-3"><path d="M7 8a3 3 0 100-6 3 3 0 000 6zM14.5 9a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM1.615 16.428a1.224 1.224 0 01-.569-1.175 6.002 6.002 0 0111.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 017 18a9.953 9.953 0 01-5.385-1.572zM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 00-1.588-3.755 4.502 4.502 0 015.874 2.636.818.818 0 01-.36.98A7.465 7.465 0 0114.5 16z"/></svg>';

@Component({
  selector: 'app-template-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    BadgeComponent,
    TemplateCategoryModalComponent,
    HasPermissionDirective,
    DisableWithoutPermissionDirective,
  ],
  templateUrl: './template-list.component.html',
})
export class TemplateListComponent implements OnInit, OnDestroy {
  showCategoryModal = false;

  templates: Template[] = [];
  filteredTemplates: Template[] = [];
  paginated: Template[] = [];

  categories: TemplateCategory[] = [];
  loading = false;
  error = '';

  // Filtros
  filtroSearch = '';
  filtroStatus: TemplateStatus | '' = '';
  filtroCategoryId = '';
  /** Inc 38: '' = todas, PUBLIC = solo públicas, PERSONAL = solo mías. */
  filtroVisibility: '' | 'PUBLIC' | 'PERSONAL' = '';

  // Paginación cliente (el endpoint no pagina)
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  pageSize = 10;
  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'template-list:pageSize';

  /** Id del usuario logueado: distingue plantillas propias de públicas ajenas. */
  private currentUserId: number | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: TemplateApiService,
    private categoryApi: TemplateCategoryApiService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private auth: AuthService
  ) {
    this.currentUserId = this.auth.getCurrentUser()?.id ?? null;
  }

  /** true si la plantilla la creó el usuario actual (puede editar/borrar/cambiar estado). */
  esMia(t: Template): boolean {
    return this.currentUserId != null && t.createdBy === this.currentUserId;
  }

  ngOnInit(): void {
    const saved = localStorage.getItem(this.PAGE_SIZE_KEY);
    if (saved) this.pageSize = +saved;
    this.cargarCategorias();
    this.cargar();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargarCategorias(): void {
    this.categoryApi
      .list()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => (this.categories = data),
        error: () => {},
      });
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.api
      .searchTemplates({
        status: this.filtroStatus || undefined,
        categoryId: this.filtroCategoryId || undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.templates = data ?? [];
          this.aplicarFiltrosCliente();
          this.loading = false;
        },
        error: (err) => {
          this.error = err.error?.message || 'Error cargando plantillas';
          this.loading = false;
        },
      });
  }

  private aplicarFiltrosCliente(): void {
    const term = this.filtroSearch.trim().toLowerCase();
    let data = [...this.templates];
    if (term) {
      data = data.filter(
        (t) =>
          t.name.toLowerCase().includes(term) ||
          (t.subject ?? '').toLowerCase().includes(term)
      );
    }
    // Inc 38: filtro por visibilidad (cliente — isPublic/createdBy ya vienen en la lista).
    if (this.filtroVisibility === 'PUBLIC') {
      data = data.filter((t) => t.isPublic);
    } else if (this.filtroVisibility === 'PERSONAL') {
      data = data.filter((t) => this.esMia(t));
    }
    this.filteredTemplates = data;
    this.globalTotalElements = data.length;
    this.globalTotalPages = Math.max(1, Math.ceil(data.length / this.pageSize));
    if (this.globalPage >= this.globalTotalPages) this.globalPage = 0;
    const start = this.globalPage * this.pageSize;
    this.paginated = data.slice(start, start + this.pageSize);
  }

  buscar(): void {
    this.globalPage = 0;
    this.aplicarFiltrosCliente();
  }

  onFilterChange(): void {
    this.globalPage = 0;
    this.cargar();
  }

  limpiarFiltro(): void {
    this.filtroSearch = '';
    this.filtroStatus = '';
    this.filtroCategoryId = '';
    this.filtroVisibility = '';
    this.globalPage = 0;
    this.cargar();
  }

  onPageSizeChange(): void {
    localStorage.setItem(this.PAGE_SIZE_KEY, this.pageSize.toString());
    this.globalPage = 0;
    this.aplicarFiltrosCliente();
  }

  goToPage(p: number): void {
    if (p < 0 || p >= this.globalTotalPages) return;
    this.globalPage = p;
    this.aplicarFiltrosCliente();
  }

  nuevo(): void {
    this.router.navigate(['/templates/crear']);
  }

  editar(t: Template): void {
    this.router.navigate(['/templates/editar', t.idTemplate]);
  }

  abrirCategorias(): void {
    this.showCategoryModal = true;
  }

  onCategoryModalClosed(): void {
    this.showCategoryModal = false;
    this.cargarCategorias();
  }

  duplicar(t: Template): void {
    this.api.duplicateTemplate(t.idTemplate).subscribe({
      next: () => { this.cargar(); this.toast.created('Plantilla'); },
      error: (err) =>
        this.confirmDialog.alert({
          title: 'Error',
          message: err.error?.message || 'No se pudo duplicar la plantilla',
          variant: 'danger',
        }),
    });
  }

  async cambiarStatus(t: Template, nuevo: TemplateStatus): Promise<void> {
    if (t.status === nuevo) return;
    if (!this.esMia(t)) return;
    this.api.changeStatus(t.idTemplate, nuevo).subscribe({
      next: () => { this.cargar(); this.toast.updated('Plantilla'); },
      error: (err) =>
        this.confirmDialog.alert({
          title: 'Error',
          message: err.error?.message || 'No se pudo cambiar el estado',
          variant: 'danger',
        }),
    });
  }

  async eliminar(t: Template): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar plantilla',
      message: `¿Eliminar "${t.name}"? Las imágenes referenciadas no se borran. Esta acción no se puede deshacer.`,
    });
    if (!ok) return;
    this.api.deleteTemplate(t.idTemplate).subscribe({
      next: () => { this.cargar(); this.toast.deleted('Plantilla'); },
      error: (err) => { this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo eliminar la plantilla' }); },
    });
  }

  getCategoriaNombre(id?: string | null): string {
    if (!id) return '—';
    return this.categories.find((c) => c.idCategory === id)?.name ?? '—';
  }

  statusColor(status: TemplateStatus): 'primary' | 'success' | 'light' {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'DRAFT':
        return 'primary';
      case 'ARCHIVED':
      default:
        return 'light';
    }
  }

  statusLabel(status: TemplateStatus): string {
    switch (status) {
      case 'ACTIVE':
        return 'Activa';
      case 'DRAFT':
        return 'Borrador';
      case 'ARCHIVED':
        return 'Archivada';
    }
  }

  visibilityLabel(t: Template): string {
    return t.isPublic ? 'Pública' : 'Personal';
  }

  visibilityColor(t: Template): 'success' | 'dark' {
    return t.isPublic ? 'success' : 'dark';
  }

  visibilityIcon(t: Template): string {
    return t.isPublic ? VIS_ICON_PUBLIC : VIS_ICON_PERSONAL;
  }

  trackById = (_: number, t: Template) => t.idTemplate;
}
